import React from 'react';
import { Link } from 'react-router-dom';
import StatCard from '../components/common/StatCard';
import StatusBadge from '../components/common/StatusBadge';
import { PageLoader } from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { useFetch } from '../hooks/useFetch';
import { merchant as merchantApi } from '../services/api';
import { formatSats, formatUSD, formatDate, truncateTxId, explorerLink, formatPercent } from '../utils/format';

export default function Dashboard() {
  const { data: stats, loading: statsLoading, error: statsError, refetch: refetchStats } = useFetch(merchantApi.stats);
  const { data: paymentsData, loading: paymentsLoading } = useFetch(() => merchantApi.payments({ limit: 10 }));
  const { data: chargebacksData, loading: chargebacksLoading } = useFetch(() => merchantApi.chargebacks({ limit: 5 }));

  if (statsLoading) return <PageLoader />;
  if (statsError) return (
    <div className="p-8">
      <ErrorMessage message={statsError} onRetry={refetchStats} />
    </div>
  );

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Overview</h1>
        <p className="text-sm text-gray-400 mt-1">Your MintBridge merchant dashboard</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Total Payments"
          value={stats?.total_payments ?? '—'}
          sub="all time"
          accent="blue"
          icon={
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          }
        />
        <StatCard
          label="Total Volume"
          value={formatSats(stats?.total_volume_sats)}
          sub={formatUSD(stats?.total_volume_usd_cents)}
          accent="green"
          icon={
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          }
        />
        <StatCard
          label="Escrow Balance"
          value={formatSats(stats?.escrow_balance_sats)}
          sub={`${formatSats(stats?.escrow_balance_eligible_sats)} eligible`}
          accent="yellow"
          icon={
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          }
        />
        <StatCard
          label="Chargebacks"
          value={stats?.chargeback_count ?? '—'}
          sub={`${formatPercent(stats?.chargeback_rate)} of payments`}
          accent="red"
          icon={
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          }
        />
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Recent payments */}
        <div className="col-span-2">
          <div className="card p-0 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
              <h2 className="text-base font-semibold text-white">Recent Payments</h2>
              <Link to="/dashboard/payments" className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                View all
              </Link>
            </div>
            {paymentsLoading ? (
              <div className="py-8"><PageLoader /></div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="table-header">Date</th>
                    <th className="table-header">Amount</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">TX ID</th>
                  </tr>
                </thead>
                <tbody>
                  {(paymentsData?.payments || []).slice(0, 8).map(p => (
                    <tr key={p.id} className="table-row">
                      <td className="table-cell text-gray-400 whitespace-nowrap">{formatDate(p.created_at)}</td>
                      <td className="table-cell">
                        <div className="font-medium text-gray-200">{formatUSD(p.amount_cents)}</div>
                        <div className="text-xs text-gray-500">{formatSats(p.amount_sats)}</div>
                      </td>
                      <td className="table-cell"><StatusBadge status={p.status} /></td>
                      <td className="table-cell">
                        {p.tx_id ? (
                          <a
                            href={explorerLink(p.tx_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-xs text-blue-400 hover:text-blue-300 transition-colors"
                            title={p.tx_id}
                          >
                            {truncateTxId(p.tx_id)}
                          </a>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-6">
          {/* Escrow summary */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-white">Escrow Summary</h2>
              <Link to="/dashboard/escrow" className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                Details
              </Link>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">Pending ({"<"}10 confs)</span>
                <span className="text-sm font-medium text-yellow-400">{formatSats(stats?.escrow_balance_pending_sats)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">Eligible for release</span>
                <span className="text-sm font-medium text-green-400">{formatSats(stats?.escrow_balance_eligible_sats)}</span>
              </div>
              <div className="h-px bg-gray-800" />
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-300">Total in escrow</span>
                <span className="text-sm font-bold text-gray-100">{formatSats(stats?.escrow_balance_sats)}</span>
              </div>
            </div>

            {/* Progress bar */}
            {stats && (
              <div className="mt-4">
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full transition-all duration-500"
                    style={{
                      width: stats.escrow_balance_sats > 0
                        ? `${(stats.escrow_balance_eligible_sats / stats.escrow_balance_sats) * 100}%`
                        : '0%'
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Eligible</span>
                  <span>Pending</span>
                </div>
              </div>
            )}
          </div>

          {/* Recent chargebacks */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-white">Chargebacks</h2>
              <Link to="/dashboard/chargebacks" className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                View all
              </Link>
            </div>
            {chargebacksLoading ? (
              <div className="py-4"><PageLoader /></div>
            ) : (chargebacksData?.chargebacks || []).length === 0 ? (
              <div className="text-center py-6">
                <div className="text-green-400 text-2xl mb-2">✓</div>
                <p className="text-sm text-gray-500">No chargebacks</p>
              </div>
            ) : (
              <div className="space-y-2">
                {(chargebacksData?.chargebacks || []).map(cb => (
                  <div key={cb.id} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0">
                    <div>
                      <div className="text-sm text-gray-200">{formatUSD(cb.amount_cents)}</div>
                      <div className="text-xs text-gray-500">{formatDate(cb.created_at)}</div>
                    </div>
                    <StatusBadge status={cb.status} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
